import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Save, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { teamsNotificationApi } from '@/lib/api/admin';
import type { AppSettings, UpdateSettingsRequest } from '@/types';

const settingsSchema = z.object({
  defaultTimeoutSeconds: z.number().min(30).max(3600),
  maxRetryAttempts: z.number().min(0).max(10),
  retryDelaySeconds: z.number().min(10).max(600),
  enableEmailNotifications: z.boolean(),
  enableTeamsNotifications: z.boolean(),
  teamsWebhookUrl: z.string().url().nullable().or(z.literal('')),
  notificationRecipients: z.string(),
  bridgeBaseUrl: z.string().url().or(z.literal('')),
  bridgeApiKeySecretName: z.string(),
  amplifyBaseUrl: z.string().url().or(z.literal('')),
  amplifyApiKeySecretName: z.string(),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

interface SettingsFormProps {
  settings: AppSettings;
  onSubmit: (data: UpdateSettingsRequest) => void;
  isSubmitting?: boolean | undefined;
}

export function SettingsForm({ settings, onSubmit, isSubmitting }: SettingsFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      defaultTimeoutSeconds: settings.defaultTimeoutSeconds,
      maxRetryAttempts: settings.maxRetryAttempts,
      retryDelaySeconds: settings.retryDelaySeconds,
      enableEmailNotifications: settings.enableEmailNotifications,
      enableTeamsNotifications: settings.enableTeamsNotifications,
      teamsWebhookUrl: settings.teamsWebhookUrl ?? '',
      notificationRecipients: settings.notificationRecipients.join(', '),
      bridgeBaseUrl: settings.bridgeBaseUrl,
      bridgeApiKeySecretName: settings.bridgeApiKeySecretName,
      amplifyBaseUrl: settings.amplifyBaseUrl,
      amplifyApiKeySecretName: settings.amplifyApiKeySecretName,
    },
  });

  const enableEmailNotifications = watch('enableEmailNotifications');
  const enableTeamsNotifications = watch('enableTeamsNotifications');
  const teamsWebhookUrl = watch('teamsWebhookUrl');

  const { toast } = useToast();

  const testNotification = useMutation({
    mutationFn: (webhookUrl: string) => teamsNotificationApi.sendTest(webhookUrl),
    onSuccess: (data) => {
      if (data.isSuccess) {
        toast({ title: 'Test notification sent', description: 'Check your Teams channel for the message.' });
      } else {
        toast({ title: 'Notification failed', description: data.errorMessage ?? 'Unknown error', variant: 'destructive' });
      }
    },
    onError: () => {
      toast({ title: 'Request failed', description: 'Could not reach the server.', variant: 'destructive' });
    },
  });

  const handleFormSubmit = (data: SettingsFormValues) => {
    const update: UpdateSettingsRequest = {};

    if (data.defaultTimeoutSeconds !== settings.defaultTimeoutSeconds)
      update.defaultTimeoutSeconds = data.defaultTimeoutSeconds;
    if (data.maxRetryAttempts !== settings.maxRetryAttempts)
      update.maxRetryAttempts = data.maxRetryAttempts;
    if (data.retryDelaySeconds !== settings.retryDelaySeconds)
      update.retryDelaySeconds = data.retryDelaySeconds;
    if (data.enableEmailNotifications !== settings.enableEmailNotifications)
      update.enableEmailNotifications = data.enableEmailNotifications;
    if (data.enableTeamsNotifications !== settings.enableTeamsNotifications)
      update.enableTeamsNotifications = data.enableTeamsNotifications;

    const newWebhookUrl = data.teamsWebhookUrl || null;
    if (newWebhookUrl !== settings.teamsWebhookUrl)
      update.teamsWebhookUrl = newWebhookUrl;

    const newRecipients = data.notificationRecipients
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    if (newRecipients.join(', ') !== settings.notificationRecipients.join(', '))
      update.notificationRecipients = newRecipients;

    if (data.bridgeBaseUrl !== settings.bridgeBaseUrl)
      update.bridgeBaseUrl = data.bridgeBaseUrl;
    if (data.bridgeApiKeySecretName !== settings.bridgeApiKeySecretName)
      update.bridgeApiKeySecretName = data.bridgeApiKeySecretName;
    if (data.amplifyBaseUrl !== settings.amplifyBaseUrl)
      update.amplifyBaseUrl = data.amplifyBaseUrl;
    if (data.amplifyApiKeySecretName !== settings.amplifyApiKeySecretName)
      update.amplifyApiKeySecretName = data.amplifyApiKeySecretName;

    onSubmit(update);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Migration Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Migration Settings</CardTitle>
          <CardDescription>
            Configure default values for migration operations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="defaultTimeoutSeconds">Default Timeout (seconds)</Label>
              <Input
                id="defaultTimeoutSeconds"
                type="number"
                {...register('defaultTimeoutSeconds', { valueAsNumber: true })}
              />
              {errors.defaultTimeoutSeconds && (
                <p className="text-sm text-destructive">
                  {errors.defaultTimeoutSeconds.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">30 - 3600 seconds</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxRetryAttempts">Max Retry Attempts</Label>
              <Input
                id="maxRetryAttempts"
                type="number"
                {...register('maxRetryAttempts', { valueAsNumber: true })}
              />
              {errors.maxRetryAttempts && (
                <p className="text-sm text-destructive">
                  {errors.maxRetryAttempts.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">0 - 10 attempts</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="retryDelaySeconds">Retry Delay (seconds)</Label>
              <Input
                id="retryDelaySeconds"
                type="number"
                {...register('retryDelaySeconds', { valueAsNumber: true })}
              />
              {errors.retryDelaySeconds && (
                <p className="text-sm text-destructive">
                  {errors.retryDelaySeconds.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground">10 - 600 seconds</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
          <CardDescription>
            Configure how you receive migration notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Notifications */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enableEmailNotifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive email notifications for migration events
              </p>
            </div>
            <Switch
              id="enableEmailNotifications"
              checked={enableEmailNotifications}
              onCheckedChange={(checked) => setValue('enableEmailNotifications', checked, { shouldDirty: true })}
            />
          </div>

          {enableEmailNotifications && (
            <div className="space-y-2 pl-4 border-l-2 border-muted">
              <Label htmlFor="notificationRecipients">Email Recipients</Label>
              <Input
                id="notificationRecipients"
                placeholder="email1@example.com, email2@example.com"
                {...register('notificationRecipients')}
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of email addresses
              </p>
            </div>
          )}

          <Separator />

          {/* Teams Notifications */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enableTeamsNotifications">Teams Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Send notifications to a Teams channel
              </p>
            </div>
            <Switch
              id="enableTeamsNotifications"
              checked={enableTeamsNotifications}
              onCheckedChange={(checked) => setValue('enableTeamsNotifications', checked, { shouldDirty: true })}
            />
          </div>

          {enableTeamsNotifications && (
            <div className="space-y-2 pl-4 border-l-2 border-muted">
              <Label htmlFor="teamsWebhookUrl">Teams Webhook URL</Label>
              <Input
                id="teamsWebhookUrl"
                type="url"
                placeholder="https://*.webhook.office.com/webhookb2/..."
                {...register('teamsWebhookUrl')}
              />
              {errors.teamsWebhookUrl && (
                <p className="text-sm text-destructive">
                  {errors.teamsWebhookUrl.message}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!teamsWebhookUrl || testNotification.isPending}
                onClick={() => teamsWebhookUrl && testNotification.mutate(teamsWebhookUrl)}
              >
                {testNotification.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Test Notification
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* External Services */}
      <Card>
        <CardHeader>
          <CardTitle>External Services</CardTitle>
          <CardDescription>
            Configure Bridge and Amplify API connection settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Bridge */}
          <div>
            <h4 className="text-sm font-medium mb-3">Bridge</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bridgeBaseUrl">Base URL</Label>
                <Input
                  id="bridgeBaseUrl"
                  type="url"
                  placeholder="https://bridge.ministryforms.net/"
                  {...register('bridgeBaseUrl')}
                />
                {errors.bridgeBaseUrl && (
                  <p className="text-sm text-destructive">
                    {errors.bridgeBaseUrl.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="bridgeApiKeySecretName">API Key Secret Name</Label>
                <Input
                  id="bridgeApiKeySecretName"
                  placeholder="BridgeApiKey"
                  {...register('bridgeApiKeySecretName')}
                />
                <p className="text-xs text-muted-foreground">
                  Key Vault secret name for the Bridge API key
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Amplify */}
          <div>
            <h4 className="text-sm font-medium mb-3">Amplify</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amplifyBaseUrl">Base URL</Label>
                <Input
                  id="amplifyBaseUrl"
                  type="url"
                  placeholder="https://account.myamplify.io/"
                  {...register('amplifyBaseUrl')}
                />
                {errors.amplifyBaseUrl && (
                  <p className="text-sm text-destructive">
                    {errors.amplifyBaseUrl.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="amplifyApiKeySecretName">API Key Secret Name</Label>
                <Input
                  id="amplifyApiKeySecretName"
                  placeholder="AmplifyApiKey"
                  {...register('amplifyApiKeySecretName')}
                />
                <p className="text-xs text-muted-foreground">
                  Key Vault secret name for the Amplify API key
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Last Updated Info */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {settings.updatedByUserName
            ? `Last updated by ${settings.updatedByUserName} on ${new Date(settings.updatedAt).toLocaleString()}`
            : ''}
        </span>
        <Button type="submit" disabled={!isDirty || isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>
    </form>
  );
}
